<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="comparison-card-group-title" :field="fields?.title" tag="h2" />
      <JssRichText class="comparison-card-group-desc" :field="fields?.subtitle" tag="p" />
      <div class="comparison-card-group-content">
        <div class="comparison-card-group-content-card" v-if="fields?.cardList?.length > 0">
          <div
            class="comparison-card-group-content-card-item"
            v-for="item in fields?.cardList"
            :key="item?.id"
          >
            <div class="comparison-card-group-content-card-item-header">
              <div class="comparison-card-group-content-card-item-header-lt">
                <div class="comparison-card-group-content-card-item-header-lt-icon">
                  <Icon :svg="item?.fields?.icon?.value?.svgCode" />
                </div>
                <div class="comparison-card-group-content-card-item-header-lt-text">
                  <JssRichText
                    class="comparison-card-group-content-card-item-header-lt-text-version"
                    :field="item?.fields?.version"
                  />
                  <JssRichText
                    class="comparison-card-group-content-card-item-header-lt-text-versionDesc"
                    :field="item?.fields?.versionDesc"
                  />
                </div>
              </div>
              <div class="comparison-card-group-content-card-item-header-rt">
                <JssRichText
                  class="comparison-card-group-content-card-item-header-rt-text"
                  :field="item?.fields?.tip"
                />
              </div>
            </div>
            <div class="comparison-card-group-content-card-item-deliver">
              <div
                class="comparison-card-group-content-card-item-deliver-item"
                v-for="deliver in item?.fields?.deliverList"
                :key="deliver?.id"
              >
                <JssRichText
                  class="comparison-card-group-content-card-item-deliver-item-title"
                  :field="deliver?.fields?.title"
                  tag="h3"
                />
                <JssRichText
                  class="comparison-card-group-content-card-item-deliver-item-desc"
                  :field="deliver?.fields?.desc"
                />
              </div>
            </div>
            <div class="comparison-card-group-content-card-item-desc">
              <div
                class="comparison-card-group-content-card-item-desc-item"
                v-for="desc in item?.fields?.descList"
                :key="desc?.id"
              >
                <Icon :svg="fields?.descIcon?.value?.svgCode" />
                <JssRichText
                  class="comparison-card-group-content-card-item-desc-item-desc"
                  :field="desc?.fields?.text"
                />
              </div>
            </div>
            <div class="comparison-card-group-content-card-item-step">
              <JssRichText
                class="comparison-card-group-content-card-item-step-title"
                :field="item?.fields?.stepText"
                tag="h3"
              />
              <div
                class="comparison-card-group-content-card-item-step-content"
                v-if="item?.fields?.stepList?.length > 0"
              >
                <div
                  class="comparison-card-group-content-card-item-step-item"
                  v-for="(step, index) in item?.fields?.stepList"
                  :key="step?.id"
                >
                  <JssRichText
                    class="comparison-card-group-content-card-item-step-item-text"
                    :field="step?.fields?.text"
                  />
                  <Icon
                    v-if="index !== item?.fields?.stepList?.length - 1"
                    :svg="fields?.rightIcon?.value?.svgCode"
                  />
                </div>
              </div>
            </div>
            <SiteButton
              class="comparison-card-group-content-card-item-button"
              v-bind="item?.fields?.button"
            />
          </div>
        </div>
        <div class="comparison-card-group-content-footer">
          <div class="comparison-card-group-content-footer-title">
            <Icon :svg="fields?.footerIcon?.value?.svgCode" />
            <JssRichText
              class="comparison-card-group-content-footer-text"
              :field="fields?.footerText"
            />
          </div>
          <div class="comparison-card-group-content-footer-content">
            <div
              class="comparison-card-group-content-footer-content-item"
              v-for="item in fields?.footerList"
              :key="item?.id"
            >
              <Icon :svg="item?.fields?.image?.value?.svgCode" />
              <div class="comparison-card-group-content-footer-content-item-text">
                <JssRichText
                  class="comparison-card-group-content-footer-content-item-text-title"
                  :field="item?.fields?.title"
                  tag="h3"
                />
                <JssRichText
                  class="comparison-card-group-content-footer-content-item-text-desc"
                  :field="item?.fields?.desc"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed } from 'vue';
import utils from '@/utils';

export default {
  name: 'ComparisonCardGroup',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'comparison-card-group')
    );

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    return {
      rootClass,
      rootStyle,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.comparison-card-group {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
    text-align: center;
  }

  &-content {
    width: 100%;
    margin-top: 40px;

    &-card {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;

      &-item {
        width: 100%;
        padding: 16px;
        border-radius: 10px;
        border: 1px solid #e2e8f0;

        &:first-child {
          border-color: $blue-night;
          .comparison-card-group-content-card-item-deliver-item-title {
            color: $blue-night;
          }

          .comparison-card-group-content-card-item-step-item-text {
            color: $blue-night;
          }

          .comparison-card-group-content-card-item-step-item {
            .e-icon {
              fill: $blue-night;
            }
          }
        }

        &-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          &-lt {
            display: flex;
            align-items: center;

            .e-icon {
              fill: transparent;
            }

            &-icon {
              width: 32px;
              height: 32px;
              margin-right: 10px;
            }

            &-text {
              flex: 1;

              &-version {
                font-size: 16px;
                font-weight: 700;
                color: #0f172b;
              }
              &-versionDesc {
                font-size: 11px;
                font-weight: 400;
                color: #62748e;
              }
            }
          }

          &-rt {
            &-text {
              background: $blue-night;
              padding: 4px 10px;
              border-radius: 16px;
              color: $white;
              font-size: 11px;
              font-weight: 400;
            }
          }
        }

        &-deliver {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;

          &-item {
            background: #f8fafc;
            padding: 10px;
            border-radius: 8px;
            width: 100%;

            &-title {
              font-size: 24px;
              line-height: 30px;
              font-weight: 700;
              color: #90a1b9;
              text-align: center;
            }
            &-desc {
              font-size: 11px;
              line-height: 14px;
              font-weight: 400;
              color: #45556c;
              text-align: center;
            }
          }
        }

        &-desc {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;

          &-item {
            display: flex;
            align-items: center;
            gap: 6px;

            &-icon {
              width: 20px;
              height: 20px;
            }

            &-text {
              font-size: 13px;
              font-weight: 400;
              color: #314158;
            }
          }
        }

        &-step {
          margin-top: 16px;
          border-radius: 8px;
          padding: 14px 12px;
          background: #f8fafc;

          .e-icon {
            fill: #cad5e2;
          }

          &-title {
            font-size: 11px;
            line-height: 14px;
            font-weight: 600;
            color: #45556c;
          }

          &-content {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 10px;
          }

          &-item {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;
            &-last {
              icon {
                display: none;
              }
            }

            &-text {
              font-size: 11px;
              font-weight: 400;
              color: #45556c;
              background: $white;
              padding: 8px 0;
              border-radius: 4px;
              text-align: center;
              width: calc(100% - 20px);
            }
          }
        }

        &-button {
          margin-top: 12px;
          width: 100% !important;
        }
      }
    }

    &-footer {
      margin-top: 24px;
      padding: 16px;
      background: #f8fafc;

      &-title {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .e-icon {
        fill: transparent;
      }

      &-text {
        color: #0f172b;
        font-size: 16px;
        line-height: 1.5;
        font-weight: 700;
      }

      &-content {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 12px;

        &-item {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;

          icon {
            width: 28px;
            height: 28px;
          }

          &-text {
            flex: 1;

            &-title {
              font-size: 13px;
              font-weight: 600;
              color: #0f172b;
            }
            &-desc {
              font-size: 11px;
              font-weight: 400;
              color: #45556c;
            }
          }
        }
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-content {
      &-card {
        width: 100%;
        flex-direction: row;
        gap: 16px;
        margin-top: 40px;

        &-item {
          width: calc(50% - 8px);
          padding: 26px;
          border-radius: 14px;
          border: 1px solid #e2e8f0;

          &:first-child {
            border-color: $blue-night;
            .comparison-card-group-content-card-item-deliver-item-title {
              color: $blue-night;
            }

            .comparison-card-group-content-card-item-step-item-text {
              color: $blue-night;
            }

            .comparison-card-group-content-card-item-step-item {
              .e-icon {
                fill: $blue-night;
              }
            }
          }

          &-header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            &-lt {
              display: flex;
              align-items: center;

              .e-icon {
                fill: transparent;
              }

              &-icon {
                width: 40px;
                height: 40px;
                margin-right: 12px;
              }

              &-text {
                flex: 1;

                &-version {
                  font-size: 20px;
                  font-weight: 700;
                  color: #0f172b;
                }
                &-versionDesc {
                  font-size: 12px;
                  font-weight: 400;
                  color: #62748e;
                }
              }
            }

            &-rt {
              &-text {
                background: $blue-night;
                padding: 5px 12px;
                border-radius: 20px;
                color: $white;
                font-size: 12px;
                font-weight: 400;
              }
            }
          }

          &-deliver {
            margin-top: 24px;
            gap: 16px;
            flex-direction: row;

            &-item {
              background: #f8fafc;
              padding: 12px;
              border-radius: 10px;
              width: calc(50% - 8px);

              &-title {
                font-size: 30px;
                line-height: 36px;
                font-weight: 700;
                color: #90a1b9;
                text-align: center;
              }
              &-desc {
                font-size: 12px;
                line-height: 16px;
                font-weight: 400;
                color: #45556c;
                text-align: center;
              }
            }
          }

          &-desc {
            margin-top: 24px;
            display: flex;
            flex-direction: column;
            gap: 12px;

            &-item {
              display: flex;
              align-items: center;
              gap: 8px;

              &-icon {
                width: 24px;
                height: 24px;
              }

              &-text {
                font-size: 14px;
                font-weight: 400;
                color: #314158;
              }
            }
          }

          &-step {
            margin-top: 24px;
            border-radius: 10px;
            padding: 17px 16px;
            background: #f8fafc;

            .e-icon {
              fill: #cad5e2;
            }

            &-title {
              font-size: 12px;
              line-height: 16px;
              font-weight: 600;
              color: #45556c;
            }

            &-content {
              flex-direction: row;
              gap: 12px;
              margin-top: 12px;
            }

            &-item {
              display: flex;
              align-items: center;
              gap: 12px;
              flex: 1;
              &-last {
                icon {
                  display: none;
                }
              }

              &-text {
                font-size: 12px;
                font-weight: 400;
                color: #45556c;
                background: $white;
                padding: 10px 0;
                border-radius: 4px;
                text-align: center;
                width: calc(100% - 24px);
              }
            }
          }

          &-button {
            margin-top: 16px;
          }
        }
      }

      &-footer {
        margin-top: 40px;
        padding: 24px;
        background: #f8fafc;

        &-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .e-icon {
          fill: transparent;
        }

        &-text {
          color: #0f172b;
          font-size: 18px;
          line-height: 1.5;
          font-weight: 700;
        }

        &-content {
          flex-direction: row;
          gap: 16px;
          justify-content: space-between;
          margin-top: 16px;

          &-item {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 12px;

            icon {
              width: 32px;
              height: 32px;
            }

            &-text {
              flex: 1;

              &-title {
                font-size: 14px;
                font-weight: 600;
                color: #0f172b;
              }
              &-desc {
                font-size: 12px;
                font-weight: 400;
                color: #45556c;
              }
            }
          }
        }
      }
    }
  }
}
</style>
